import { isMultienum } from 'customer-data-objects/property/PropertyIdentifier';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { useCallback, useMemo, useState } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { isEligibleCpControllingProperty } from '../../conditionalLogic/utils';
import { difference, getConditionalPropertyDependencies, isAssociationField } from '../../form/utils';
import { useLeadStageChangeValidation } from '../../input/pipeline/hooks/leads/useLeadStageChangeValidation';
import ConditionalPropertiesPanel from '../../panels/ConditionalPropertiesPanel';
import { getOwnProperty } from '../../utils/getOwnProperty';
import { getConditionalPropertyOptionsRulesWithDependentErrors } from '../conditionalPropertyOptionsRules/getConditionalPropertyOptionsRulesWithDependentErrors';
import { getActiveRules, getParsedPropertyValueAsArray } from '../conditionalPropertyOptionsRules/utils';
import { useConditionalPropertiesPanelData } from './useConditionalPropertiesPanelData';
import { useGetIsUngatedForConditionalAssociations } from './useGetIsUngatedForConditionalAssociations';
import { jsx as _jsx } from "react/jsx-runtime";
const propertyNameToObject = propertyName => ({
  propertyName
});
const initialState = {
  isOpen: false,
  values: {},
  properties: []
};
const useConditionalPropertiesPanel = ({
  enableConditionalPropertiesPanel = true,
  enableDependentAssociations = false,
  conditionalPropertyRules,
  onChange,
  onApply = emptyFunction,
  onCancel = emptyFunction,
  property,
  objectTypeId,
  objectId,
  getAdditionalPropertyValue,
  showAutoSaveText,
  value,
  renderInput
}) => {
  const {
    leadStageValidationModal,
    onChangeWithLeadStageValidation
  } = useLeadStageChangeValidation({
    onChange,
    onApply,
    onCancel,
    objectTypeId,
    objectId,
    getAdditionalPropertyValue
  });
  const hasDependentAssociationsAccess = useGetIsUngatedForConditionalAssociations({
    enableDependentAssociations
  });
  const [{
    isOpen,
    values,
    properties
  }, setState] = useState(initialState);
  const {
    conditionalPropertyRulesForObjectType,
    conditionalPropertyOptionsRulesForProperty,
    allPropertiesForObjectType,
    allPropertyValues,
    loading,
    error
  } = useConditionalPropertiesPanelData({
    objectTypeId,
    property,
    objectId,
    providedConditionalPropertyRules: conditionalPropertyRules,
    enableConditionalPropertiesPanel,
    hasDependentAssociationsAccess
  });
  const closePanel = useCallback(() => {
    setState({
      isOpen: false,
      values: {},
      properties: []
    });
  }, []);
  const openPanel = useCallback((_values, _properties) => {
    setState({
      isOpen: true,
      values: _values,
      properties: _properties
    });
  }, []);
  const maybeOpenConditionalPropertiesPanel = useCallback(changes => {
    // for purposes of calculating what CP rules this edit triggered:
    // 1. only consider values changed as part of this edit
    // 2. when editing a multi-enum, only consider newly-added values.
    //    (otherwise, existing values with CP rules would trigger the CP panel
    //    to appear repeatedly when other values are added/removed)
    const getFormValue = propertyName => {
      const nextValue = getOwnProperty(changes, propertyName);
      if (propertyName !== property.name || !isMultienum(property) || !nextValue || !value) {
        return nextValue;
      }
      const prevValues = new Set(parseMultiEnumValue(value));
      const nextValues = new Set(parseMultiEnumValue(nextValue));
      const added = difference(nextValues, prevValues);
      // callback runs each time an option is selected, so we would expect at
      // most one new value added at a time. on deselection, the `added` array
      // will be empty and we don't have to show CPP
      return added[0];
    };
    const getFormValueWithAdditionalValues = propertyName => {
      if (Object.prototype.hasOwnProperty.call(changes, propertyName)) {
        return changes[propertyName];
      }
      const additionalPropertyValue = getAdditionalPropertyValue(propertyName, objectId, objectTypeId);
      if (additionalPropertyValue !== undefined) {
        return additionalPropertyValue;
      }
      return allPropertyValues ? getOwnProperty(allPropertyValues, propertyName) : undefined;
    };
    const _conditionalPropertyRules = conditionalPropertyRules || conditionalPropertyRulesForObjectType || [];
    const filteredConditionalPropertyRules = hasDependentAssociationsAccess ? _conditionalPropertyRules.map(rule => {
      return Object.assign({}, rule, {
        dependentFields: rule.dependentFields.filter(field => !isAssociationField(field))
      });
    }) : _conditionalPropertyRules;
    const conditionalPropertyDependencies = getConditionalPropertyDependencies(objectTypeId, getFormValue, {
      [property.name]: property
    }, filteredConditionalPropertyRules,
    // See comment in getConditionalPropertyDependencies
    // This is normally used to solve for a case that the CP panel doesn't encounter
    new Set(), false);
    const activeConditionalPropertyOptionsRules = getActiveRules(getParsedPropertyValueAsArray(property, changes[property.name]), conditionalPropertyOptionsRulesForProperty);
    const dependentFieldValues = activeConditionalPropertyOptionsRules.reduce((acc, rule) => {
      acc[rule.dependentFieldName] = getFormValueWithAdditionalValues(rule.dependentFieldName);
      return acc;
    }, {});
    const activeConditionalPropertyOptionsRulesWithErrors = getConditionalPropertyOptionsRulesWithDependentErrors(activeConditionalPropertyOptionsRules, allPropertiesForObjectType, dependentFieldValues);
    const activeControllingProperties = Object.keys(conditionalPropertyDependencies);
    const shouldOpenPanel = activeControllingProperties.length > 0 || activeConditionalPropertyOptionsRulesWithErrors.length > 0;
    if (shouldOpenPanel) {
      // if there are no CP dependents, we just show panel with root input
      // and form takes care of adding CPO dependents to the tree
      const initialProperties = activeControllingProperties.length > 0 ? activeControllingProperties.map(propertyNameToObject) : [{
        propertyName: property.name
      }];
      openPanel(changes, initialProperties);
    } else {
      onChangeWithLeadStageValidation(changes);
    }
  }, [hasDependentAssociationsAccess, conditionalPropertyRules, conditionalPropertyRulesForObjectType, objectTypeId, property, conditionalPropertyOptionsRulesForProperty, allPropertiesForObjectType, value, getAdditionalPropertyValue, objectId, allPropertyValues, openPanel, onChangeWithLeadStageValidation]);
  const memoizedDefault = useMemo(() => {
    return {
      maybeOpenConditionalPropertiesPanel: onChangeWithLeadStageValidation,
      conditionalPropertiesPanel: null,
      loading: false,
      error: undefined
    };
  }, [onChangeWithLeadStageValidation]);
  return useMemo(() => {
    if ((!isEligibleCpControllingProperty(property) || !enableConditionalPropertiesPanel) && !leadStageValidationModal) {
      return memoizedDefault;
    }
    const conditionalPropertiesPanel = isOpen ? /*#__PURE__*/_jsx(ConditionalPropertiesPanel, {
      properties: properties,
      onApply: changes => {
        onChangeWithLeadStageValidation(changes);
        onApply();
        closePanel();
      },
      onCancel: () => {
        onCancel();
        closePanel();
      },
      objectTypeId: objectTypeId,
      objectId: objectId,
      getAdditionalPropertyValue: getAdditionalPropertyValue,
      triggerPropertyDefinition: property,
      defaultValues: values,
      showAutoSaveText: showAutoSaveText,
      renderInput: renderInput,
      enableDependentAssociations: enableDependentAssociations
    }) : null;
    return {
      conditionalPropertiesPanel: conditionalPropertiesPanel || leadStageValidationModal,
      maybeOpenConditionalPropertiesPanel,
      loading,
      error
    };
  }, [closePanel, enableConditionalPropertiesPanel, enableDependentAssociations, error, getAdditionalPropertyValue, isOpen, leadStageValidationModal, loading, maybeOpenConditionalPropertiesPanel, memoizedDefault, objectId, objectTypeId, onApply, onCancel, onChangeWithLeadStageValidation, properties, property, renderInput, showAutoSaveText, values]);
};
export default useConditionalPropertiesPanel;